import { useState, useEffect } from 'react'
import { useModal } from '../../contexts/ModalContext'
import { useCampaign } from '../../contexts/CampaignContext'
import { useAuth } from '../../contexts/AuthContext'
import AdminControls from '../AdminControls'
import ItemForm from '../forms/ItemForm'
import MarkdownText from '../MarkdownText'
import ViewToggle, { usePersistedViewMode } from '../ViewToggle'
import EntityTable from '../EntityTable'
import { buildDdbPayload, parseDdbSpec, summarizeDdbSpec } from '../../utils/ddbExport'
import { isDdbUrl, DDB_CREATE_ITEM_URL } from '../../../shared/ddb-options.js'

// DM-only marker on cards/rows: has the item been imported into D&D Beyond
// (a ddb_url is set), or does it only have stats ready to import? Players never
// receive either column, so this renders nothing for them.
function DdbBadge({ item }) {
  if (!item.ddb_spec) return null
  const imported = isDdbUrl(item.ddb_url)
  return (
    <span
      title={imported ? 'Imported into D&D Beyond' : 'Has D&D Beyond stats, not imported yet'}
      style={{ marginLeft: '0.5rem', fontSize: '0.65rem', background: imported ? '#8e2c2c' : 'transparent', color: 'var(--text-primary)', border: '1px solid #c0392b', padding: '0.15rem 0.45rem', borderRadius: '3px', whiteSpace: 'nowrap' }}
    >
      {imported ? 'DDB ✓' : 'DDB'}
    </span>
  )
}

function ItemsTab({ focusId, onConsumeFocus }) {
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [editingItem, setEditingItem] = useState(null)
  const [isCreating, setIsCreating] = useState(false)
  const [selectedItem, setSelectedItem] = useState(null)
  const [viewMode, setViewMode] = usePersistedViewMode('view:items')
  const { canEdit } = useCampaign()
  const { alert: showAlert, confirm } = useModal()
  const { authEpoch } = useAuth()

  // authEpoch dependency: if this tab was already mounted (and its fetch
  // already 401'd into the `error` state) when the session expired, signing
  // back in via the session-expired modal must retry this fetch — otherwise
  // the tab is stuck showing "Error loading items" until a manual refresh,
  // which is exactly the incident this exists to prevent. A fresh mount
  // already refetches on its own; this only matters for a tab that was
  // already sitting on screen.
  useEffect(() => {
    fetchItems()
  }, [authEpoch])

  // Open a specific item when navigated to via a [[wiki-link]].
  useEffect(() => {
    if (!focusId || loading) return
    const target = items.find((i) => i.id === focusId)
    if (target) {
      setSelectedItem(target)
      onConsumeFocus?.()
    }
  }, [focusId, loading, items])

  useEffect(() => {
    if (!selectedItem) return

    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        setSelectedItem(null)
      }
    }

    document.addEventListener('keydown', handleEscape)
    document.body.style.overflow = 'hidden'

    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.body.style.overflow = 'unset'
    }
  }, [selectedItem])

  const fetchItems = () => {
    // A 401 (or any non-OK status) still has a JSON body (`{error: "..."}`),
    // so an unguarded res.json() "succeeds" with that object instead of an
    // array — setItems() would then hand items.map() a non-array and crash
    // the render entirely. Route non-OK responses to the .catch below
    // instead, and clear any previous error so a retry that DOES succeed
    // (e.g. the authEpoch-triggered refetch after signing back in) actually
    // reaches the item list instead of getting stuck on a stale error screen.
    fetch('/api/items')
      .then(res => (res.ok ? res.json() : Promise.reject(new Error('Failed to load items'))))
      .then(data => {
        setItems(Array.isArray(data) ? data : [])
        setError(null)
        setLoading(false)
      })
      .catch(err => {
        setError(err.message)
        setLoading(false)
      })
  }

  const handleEdit = (item) => {
    setEditingItem(item)
  }

  const handleCreate = () => {
    setIsCreating(true)
  }

  const handleFormSubmit = async (formData, iconFile, deleteIcon) => {
    try {
      const isEdit = !!editingItem
      const response = await fetch('/api/items', {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(isEdit ? { ...formData, id: editingItem.id } : formData)
      })

      if (response.ok) {
        const result = await response.json()

        // Get the item ID (either from editing or from the newly created item)
        const itemId = isEdit ? editingItem.id : result.id

        // Handle icon deletion
        if (deleteIcon) {
          const deleteResponse = await fetch('/api/items/delete-icon', {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ item_id: itemId })
          })

          if (!deleteResponse.ok) {
            console.error('Failed to delete icon')
          }
        }
        // Handle icon upload
        else if (iconFile) {
          const uploadFormData = new FormData()
          uploadFormData.append('icon', iconFile)
          uploadFormData.append('item_id', itemId)

          const uploadResponse = await fetch('/api/items/upload-icon', {
            method: 'POST',
            body: uploadFormData
          })

          if (!uploadResponse.ok) {
            const error = await uploadResponse.json()
            showAlert({
              title: 'Warning',
              message: `Item saved but icon upload failed: ${error.error}`,
              type: 'error'
            })
          }
        }

        await fetchItems()
        showAlert({
          title: isEdit ? 'Item Updated' : 'Item Created',
          message: `Item "${formData.name}" has been ${isEdit ? 'updated' : 'created'} successfully.`,
          type: 'success'
        })
        setEditingItem(null)
        setIsCreating(false)
      } else {
        const error = await response.json()
        showAlert({
          title: 'Error',
          message: error.error || `Failed to ${isEdit ? 'update' : 'create'} item`,
          type: 'error'
        })
      }
    } catch (err) {
      showAlert({
        title: 'Error',
        message: `Failed to ${editingItem ? 'update' : 'create'} item`,
        type: 'error'
      })
    }
  }

  // Copy first, then open: writing the clipboard needs this tab to still have
  // focus, and a new tab takes it. `open` stays within the click's user
  // activation, so the popup isn't blocked.
  const handleCopyForDdb = async (item, { open = false } = {}) => {
    const payload = buildDdbPayload(item)
    if (!payload) return
    try {
      await navigator.clipboard.writeText(JSON.stringify(payload, null, 2))
    } catch (err) {
      showAlert({
        title: 'Copy failed',
        message: `Your browser blocked clipboard access (${err?.message || 'unknown error'}).`,
        type: 'error'
      })
      return
    }
    // Not the 'noopener' feature string: with it, window.open always returns
    // null, so a blocked popup would be indistinguishable from an opened one.
    const opened = open ? window.open(DDB_CREATE_ITEM_URL, '_blank') : null
    if (opened) opened.opener = null
    showAlert({
      title: 'Copied for D&D Beyond',
      message: `${opened ? 'D&D Beyond is open in a new tab.' : 'Open D&D Beyond → Homebrew → Create a Magic Item.'} In the Start Your Quest panel there, click "Paste clipboard", then "Start import". Afterwards, paste the new item's D&D Beyond link into this item's Edit form.`,
      type: 'success'
    })
  }

  const handleDelete = async (item) => {
    await confirm({
      title: 'Delete Item',
      message: `Are you sure you want to delete "${item.name}"? This action cannot be undone.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      onConfirm: async () => {
        try {
          const response = await fetch('/api/items', {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: item.id })
          })

          if (response.ok) {
            setItems(items.filter(i => i.id !== item.id))
            showAlert({
              title: 'Item Deleted',
              message: `"${item.name}" has been deleted.`,
              type: 'success'
            })
          } else {
            const error = await response.json()
            showAlert({
              title: 'Error',
              message: error.error || 'Failed to delete item',
              type: 'error'
            })
          }
        } catch (err) {
          showAlert({
            title: 'Error',
            message: 'Failed to delete item',
            type: 'error'
          })
        }
      }
    })
  }

  const handleToggleVisibility = async (item) => {
    const action = item.is_visible ? 'hide' : 'reveal'
    await confirm({
      title: `${action === 'hide' ? 'Hide' : 'Reveal'} Item`,
      message: `${action === 'hide' ? 'Hide' : 'Reveal'} "${item.name}" ${action === 'hide' ? 'from' : 'to'} players?`,
      confirmText: action === 'hide' ? 'Hide' : 'Reveal',
      onConfirm: async () => {
        try {
          const response = await fetch('/api/items', {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: item.id, is_visible: item.is_visible ? 0 : 1 })
          })

          if (response.ok) {
            setItems(items.map(i =>
              i.id === item.id ? { ...i, is_visible: item.is_visible ? 0 : 1 } : i
            ))
            showAlert({
              title: 'Visibility Updated',
              message: `"${item.name}" is now ${action === 'hide' ? 'hidden from' : 'visible to'} players.`,
              type: 'success'
            })
          } else {
            const error = await response.json()
            showAlert({
              title: 'Error',
              message: error.error || 'Failed to update visibility',
              type: 'error'
            })
          }
        } catch (err) {
          showAlert({
            title: 'Error',
            message: 'Failed to update visibility',
            type: 'error'
          })
        }
      }
    })
  }

  if (loading) {
    return (
      <div className="tab-pane">
        <div className="page-header">
          <h1>Items & Inventory</h1>
          <p className="page-subtitle">Quest items and important artifacts</p>
        </div>
        <div style={{ background: 'var(--card-bg)', padding: '3rem', borderRadius: '12px', border: '1px solid var(--border-color)', textAlign: 'center' }}>
          <p style={{ color: 'var(--text-muted)' }}>Loading items...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="tab-pane">
        <div className="page-header">
          <h1>Items & Inventory</h1>
          <p className="page-subtitle">Quest items and important artifacts</p>
        </div>
        <div style={{ background: 'var(--card-bg)', padding: '3rem', borderRadius: '12px', border: '1px solid var(--border-color)', textAlign: 'center' }}>
          <p style={{ color: '#e74c3c' }}>Error loading items: {error}</p>
        </div>
      </div>
    )
  }

  if (items.length === 0) {
    return (
      <div className="tab-pane">
        <div className="page-header">
          <h1>Items & Inventory</h1>
          <p className="page-subtitle">Quest items and important artifacts</p>
        </div>
        <div style={{ background: 'var(--card-bg)', padding: '3rem', borderRadius: '12px', border: '1px solid var(--border-color)', textAlign: 'center' }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem', opacity: 0.5 }}>⚗️</div>
          <h3 style={{ color: 'var(--text-muted)', marginBottom: '1rem' }}>No Items Found</h3>
          <p style={{ color: 'var(--text-muted)', marginBottom: canEdit ? '1.5rem' : 0 }}>
            {canEdit ? 'Add your first item to get this campaign started.' : 'Quest items and treasures will be tracked here.'}
          </p>
          {canEdit && (
            <button
              onClick={handleCreate}
              style={{
                background: 'linear-gradient(135deg, #1a8a6e 0%, #2ecc71 100%)',
                color: '#ffffff',
                border: '1px solid #27ae60',
                padding: '0.8rem 1.5rem',
                borderRadius: '8px',
                fontWeight: 600,
                fontSize: '0.95rem',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                boxShadow: '0 2px 8px rgba(46, 204, 113, 0.3)',
              }}
            >
              + Add New Item
            </button>
          )}
        </div>

        <ItemForm
          isOpen={isCreating || !!editingItem}
          onClose={() => {
            setIsCreating(false)
            setEditingItem(null)
          }}
          item={editingItem}
          onSubmit={handleFormSubmit}
        />
      </div>
    )
  }

  // Hoisted out of the card grid's JSX so both views render exactly the same
  // rows. Players (and admins previewing as a player) never see hidden items.
  const visibleItems = items.filter(item => canEdit || item.is_visible)

  const itemColumns = [
    {
      key: 'icon',
      header: '',
      role: 'lead',
      render: (item) => {
        const isHidden = !item.is_visible && !canEdit
        return (
          <div className={`et-thumb is-square${isHidden ? ' is-unknown' : ''}`}>
            {isHidden ? '❓' : (
              item.icon_url
                ? <img src={item.icon_url} alt={item.name} />
                : (item.icon || '📦')
            )}
          </div>
        )
      },
    },
    {
      key: 'name',
      header: 'Item',
      role: 'name',
      render: (item) => {
        const isHidden = !item.is_visible && !canEdit
        return (
          <div className="et-name">
            {isHidden ? 'Unknown Item' : item.name}
            {canEdit && !item.is_visible && (
              <span style={{ marginLeft: '0.5rem', fontSize: '0.65rem', background: '#6b7f91', color: 'var(--text-primary)', padding: '0.2rem 0.5rem', borderRadius: '3px' }}>Hidden</span>
            )}
            {canEdit && <DdbBadge item={item} />}
          </div>
        )
      },
    },
    {
      key: 'type',
      header: 'Type',
      role: 'meta',
      render: (item) => {
        const isHidden = !item.is_visible && !canEdit
        if (isHidden) return null
        const label = item.item_type.replace('_', ' ')
        return (
          <span style={{ background: item.item_type === 'quest_item' ? 'var(--accent-teal)' : '#6b7f91', color: 'var(--text-primary)', padding: '0.2rem 0.6rem', borderRadius: '4px', fontSize: '0.7rem', fontWeight: 600 }}>
            {label.charAt(0).toUpperCase() + label.slice(1)}
          </span>
        )
      },
    },
    {
      key: 'description',
      header: 'Description',
      role: 'desc',
      render: (item) => {
        const isHidden = !item.is_visible && !canEdit
        return isHidden
          ? 'This item has not been discovered yet.'
          : <MarkdownText>{item.description}</MarkdownText>
      },
    },
    {
      key: 'actions',
      header: '',
      role: 'actions',
      render: (item) => canEdit && (
        <AdminControls
          item={item}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onToggleVisibility={handleToggleVisibility}
          type="item"
        />
      ),
    },
  ]

  return (
    <div className="tab-pane">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '2rem', flexWrap: 'wrap', gap: '1rem' }}>
        <div className="page-header" style={{ marginBottom: 0 }}>
          <h1>Items & Inventory</h1>
          <p className="page-subtitle">Quest items and important artifacts</p>
        </div>
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
        <ViewToggle value={viewMode} onChange={setViewMode} />
        {canEdit && (
          <button
            onClick={handleCreate}
            style={{
              background: 'linear-gradient(135deg, #1a8a6e 0%, #2ecc71 100%)',
              color: '#ffffff',
              border: '1px solid #27ae60',
              padding: '0.8rem 1.5rem',
              borderRadius: '8px',
              fontWeight: 600,
              fontSize: '0.95rem',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              boxShadow: '0 2px 8px rgba(46, 204, 113, 0.3)',
            }}
          >
            + Add New Item
          </button>
        )}
        </div>
      </div>

      {viewMode === 'table' ? (
        <EntityTable
          rows={visibleItems}
          columns={itemColumns}
          label="Items"
          onRowClick={setSelectedItem}
          isDimmed={(item) => !item.is_visible}
          emptyMessage="No items yet."
        />
      ) : (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1.5rem' }}>
        {visibleItems.map(item => {
          const isHidden = !item.is_visible && !canEdit

          return (
            <div
              key={item.id}
              onClick={() => setSelectedItem(item)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  setSelectedItem(item)
                }
              }}
              role="button"
              tabIndex={0}
              style={{
                background: 'var(--card-bg)',
                padding: '1.5rem',
                borderRadius: '12px',
                border: '1px solid var(--border-color)',
                opacity: item.is_visible ? 1 : 0.6,
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)'
                e.currentTarget.style.boxShadow = '0 8px 18px rgba(0, 0, 0, 0.25)'
                e.currentTarget.style.borderColor = 'var(--accent-teal)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)'
                e.currentTarget.style.boxShadow = 'none'
                e.currentTarget.style.borderColor = 'var(--border-color)'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem', marginBottom: '1rem' }}>
                {(isHidden || item.icon_url) && (
                  <div style={{ width: '60px', height: '60px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '8px', background: isHidden ? '#6b7f91' : 'var(--accent-deep)' }}>
                    {isHidden ? (
                      <span style={{ fontSize: '2rem' }}>❓</span>
                    ) : (
                      <img src={item.icon_url} alt={item.name} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '8px' }} />
                    )}
                  </div>
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <h4 style={{ color: 'var(--text-primary)', marginBottom: '0.3rem', wordWrap: 'break-word' }}>
                    {isHidden ? 'Unknown Item' : item.name}
                    {canEdit && !item.is_visible && <span style={{ marginLeft: '0.5rem', fontSize: '0.65rem', background: '#6b7f91', color: 'var(--text-primary)', padding: '0.2rem 0.5rem', borderRadius: '3px' }}>Hidden</span>}
                    {canEdit && <DdbBadge item={item} />}
                  </h4>
                  {!isHidden && (
                    <span style={{ background: item.item_type === 'quest_item' ? 'var(--accent-teal)' : '#6b7f91', color: 'var(--text-primary)', padding: '0.2rem 0.6rem', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 600 }}>
                      {item.item_type.replace('_', ' ').charAt(0).toUpperCase() + item.item_type.replace('_', ' ').slice(1)}
                    </span>
                  )}
                </div>
              </div>
              <div style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', lineHeight: 1.6 }}>
                {isHidden ? 'This item has not been discovered yet.' : <MarkdownText>{item.description}</MarkdownText>}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: '0.5rem', marginTop: 'auto', paddingTop: '0.75rem' }}>
                {!isHidden ? <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem', fontStyle: 'italic' }}>(click to see more)</span> : <span />}
                {canEdit && (
                  <div onClick={(e) => e.stopPropagation()} style={{ flexShrink: 0 }}>
                    <AdminControls
                      item={item}
                      onEdit={handleEdit}
                      onDelete={handleDelete}
                      onToggleVisibility={handleToggleVisibility}
                      type="item"
                    />
                  </div>
                )}
              </div>
            </div>
          )
        })}
      </div>
      )}

      {selectedItem && (() => {
        const isHidden = !selectedItem.is_visible && !canEdit

        return (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(5, 10, 18, 0.95)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 9999,
              padding: '1rem'
            }}
          >
            <div
              style={{
                background: 'linear-gradient(135deg, var(--primary-blue) 0%, var(--secondary-dark) 100%)',
                border: '2px solid var(--accent-teal)',
                borderRadius: '12px',
                maxWidth: '980px',
                width: '100%',
                boxShadow: '0 10px 40px rgba(0, 0, 0, 0.6)',
                position: 'relative',
                maxHeight: '85vh',
                display: 'flex',
                flexDirection: 'column',
                // See SessionsTab for why padding moved onto the children.
                overflow: 'hidden'
              }}
            >
              {/* Non-scrolling header row — an absolutely-positioned Close
                  button would sit on top of the scroll wrapper's scrollbar. */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '1rem 1rem 0.5rem', flexShrink: 0 }}>
                <button
                  onClick={() => setSelectedItem(null)}
                  style={{
                    background: 'linear-gradient(135deg, #3a4550 0%, #2d363f 100%)',
                    color: '#ffffff',
                    border: '1px solid #5a6570',
                    borderRadius: '8px',
                    padding: '0.6rem 1.4rem',
                    cursor: 'pointer',
                    fontSize: '1rem',
                    fontWeight: 600
                  }}
                  aria-label="Close item details"
                >
                  Close
                </button>
              </div>

              <div style={{ overflowY: 'auto', minHeight: 0, padding: '0 1rem 2rem 2rem', scrollbarGutter: 'stable' }}>
              <div className="detail-float">
                {(isHidden || selectedItem.icon_url) && (
                <div
                  className="detail-float-media"
                  style={{
                    width: '200px',
                    height: '200px',
                    borderRadius: '16px',
                    background: isHidden ? '#6b7f91' : 'var(--accent-deep)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '5rem',
                    color: 'var(--text-primary)',
                    flexShrink: 0,
                    overflow: 'hidden'
                  }}
                >
                  {isHidden ? (
                    '❓'
                  ) : (
                    <img src={selectedItem.icon_url} alt={selectedItem.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  )}
                </div>
                )}

                <div>
                  <h2 style={{ color: 'var(--text-primary)', marginBottom: '0.5rem' }}>
                    {isHidden ? 'Unknown Item' : selectedItem.name}
                  </h2>
                  {!isHidden && (
                    <span style={{ background: selectedItem.item_type === 'quest_item' ? 'var(--accent-teal)' : '#6b7f91', color: 'var(--text-primary)', padding: '0.25rem 0.6rem', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 600, display: 'inline-block', marginBottom: '1rem' }}>
                      {selectedItem.item_type.replace('_', ' ').charAt(0).toUpperCase() + selectedItem.item_type.replace('_', ' ').slice(1)}
                    </span>
                  )}
                  <div style={{ color: 'var(--text-secondary)', lineHeight: 1.7, fontSize: '1rem' }}>
                    {isHidden ? 'This item has not been discovered yet.' : <MarkdownText style={{ whiteSpace: 'pre-line' }}>{selectedItem.full_description || selectedItem.description}</MarkdownText>}
                  </div>
                </div>
              </div>
              {canEdit && parseDdbSpec(selectedItem.ddb_spec) && (
                <div style={{ marginTop: '1.5rem', padding: '1rem', border: '1px solid #c0392b', borderRadius: '8px', background: 'rgba(0, 0, 0, 0.2)' }}>
                  <div style={{ color: 'var(--text-primary)', fontWeight: 600, marginBottom: '0.35rem' }}>
                    🐉 D&amp;D Beyond <span style={{ fontWeight: 400, color: 'var(--text-muted)', fontSize: '0.8rem' }}>(DM only)</span>
                  </div>
                  <div style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginBottom: '0.75rem' }}>
                    {summarizeDdbSpec(parseDdbSpec(selectedItem.ddb_spec))}
                  </div>
                  <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'center' }}>
                    <button
                      onClick={() => handleCopyForDdb(selectedItem, { open: true })}
                      style={{ background: 'linear-gradient(135deg, #8e2c2c 0%, #c0392b 100%)', color: '#ffffff', border: '1px solid #c0392b', borderRadius: '8px', padding: '0.6rem 1.2rem', cursor: 'pointer', fontWeight: 600 }}
                    >
                      Copy &amp; open D&amp;D Beyond ↗
                    </button>
                    <button
                      onClick={() => handleCopyForDdb(selectedItem)}
                      style={{ background: 'transparent', color: 'var(--text-primary)', border: '1px solid #c0392b', borderRadius: '8px', padding: '0.6rem 1rem', cursor: 'pointer' }}
                    >
                      Copy only
                    </button>
                    {isDdbUrl(selectedItem.ddb_url) && (
                      <a href={selectedItem.ddb_url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent-teal)' }}>
                        View on D&amp;D Beyond ↗
                      </a>
                    )}
                  </div>
                </div>
              )}
              </div>
            </div>
          </div>
        )
      })()}

      <ItemForm
        isOpen={isCreating || !!editingItem}
        onClose={() => {
          setIsCreating(false)
          setEditingItem(null)
        }}
        item={editingItem}
        onSubmit={handleFormSubmit}
      />
    </div>
  )
}

export default ItemsTab
